import {
    FetchState, BulkLiquidationState,
    BulkLiquidationUploadModel, BulkLiquidationUploadListPage
} from "@/shared/types";
import { defineStore } from "pinia";
import * as drivewealthService from "@/shared/api/drivewealthService";
import * as storeHelper from "@/shared/storeHelper";
import _ from "lodash";
import { useNotifierStore } from "@/shared/store/notifier";
import { templatedFetchKeyCollectionStore } from "@/shared/store/fetchKeyedCollectionPlugin";
import Vue from "vue";
import { downloadCsvFile } from "@/shared/fileDownload";

const PageSize = 10;

const useUploadsSubStore = defineStore("bulkLiquidation_uploads", () => {
    return templatedFetchKeyCollectionStore<BulkLiquidationUploadListPage, BulkLiquidationUploadListPage>(
        drivewealthService.getBulkLiquidationFileRequests,
    );
});

export const initialState: BulkLiquidationState = {
    fileUploadFetchState: FetchState.Uninitialized,
    getFetchState: FetchState.Uninitialized
};

export const useBulkLiquidationStore = defineStore("bulkLiquidation", {
    state: (): BulkLiquidationState => (_.cloneDeep(initialState)),
    getters: {
        isUploadingFile: state => state.fileUploadFetchState === FetchState.Loading,
        uploads: () => useUploadsSubStore(),
        isPageLoaded () {
            return pageNumber => {
                const uploadsListPage = this.uploads.fetchStates[`${pageNumber}`];

                if (uploadsListPage) {
                    return uploadsListPage === FetchState.Loaded;
                }

                return false;
            };
        },
        pageTotal () {
            return pageNumber => {
                const uploadsListPage = this.uploads.items[`${pageNumber}`];

                if (uploadsListPage) {
                    return uploadsListPage.numberOfPages;
                }

                return 1;
            };
        },
        pagedUploadsById () {
            return pageNumber => {
                const uploadsListPage = this.uploads.items[`${pageNumber}`];

                if (uploadsListPage) {
                    return uploadsListPage.records;
                }

                return [];
            };
        },
    },
    actions: {
        setFileUploadFetchState (fetchState: FetchState) {
            this.fileUploadFetchState = fetchState;
        },
        uploadBulkLiquidationFile ({ partnerId, file }: { partnerId: number; file: any  }) {
            this.setFileUploadFetchState (FetchState.Loading);
            return drivewealthService.uploadBulkLiquidateFile(partnerId, file)
                .then(() => {
                    const notifierStore = useNotifierStore();
                    this.setFileUploadFetchState(FetchState.Loaded);
                    notifierStore.setSuccess("File was uploaded successfully");
                })
                .catch(err => {
                    this.setFileUploadFetchState (FetchState.Uninitialized);
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        setUploadsFetchState ({ fetchState, itemKey }:
                              {
                                  fetchState: FetchState,
                                  itemKey: string,
                                  pageNumber: number
                              }) {
            Vue.set(this.uploads.fetchStates, itemKey, fetchState);
        },
        async getUploads (
            args: {
                page: number,
                partnerId: number
            }
        ): Promise<any> {
            const itemKey = `${args.page}`;
            if (this.uploads.fetchStates[itemKey] === FetchState.Loading ||
                this.uploads.fetchStates[itemKey] === FetchState.Loaded) {
                return Promise.resolve();
            }

            return useUploadsSubStore().fetchKeyedApiCall(
                { partnerId: args.partnerId, username: null },
                itemKey,
                { page: args.page, pageSize: PageSize }
            )
                .catch(err => {
                    this.setUploadsFetchState({
                        itemKey, fetchState: FetchState.Uninitialized
                    });
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        resetUploadsState () {
            return useUploadsSubStore().$reset();
        },
        downloadBulkLiquidationStatus (params: { partnerId: number, fileNameGuid: string }): Promise<any> {
            return drivewealthService.downloadBulkLiquidationStatus(params.partnerId, params.fileNameGuid)
                .then(response => {
                    const fileName = `file-upload-current-status-${params.fileNameGuid}.csv`;
                    downloadCsvFile(response.data, fileName);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: true });
                });
        }
    }
});